// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';

// Components
import Error from '../core/error';
import Loading from '../core/loading';
import TableWithSort from '../core/table-with-sort';
import TooltipTrigger from '../core/tooltip-trigger';

// Configs
import servicesConfig from '../../../config/services-config';

// Lib
import actionsServiceCaller from '../../lib/app/actions-service-caller';

// Styles
import './fvod-ordering-table.scss';

import {columnConfig} from './helpers/constant';

class FvodOrderingTable extends Component {
    state = {
        fvodData: [],
        originalData: [],
        fetching: false,
        error: null,
        selectedGuids: {},
        contextMenu: null,
    };

    rootRef = React.createRef();

    menuRef = React.createRef();

    componentDidMount() {
        this.fetchFvodData();
        window.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('click', this.closeContextMenu);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('click', this.closeContextMenu);
    }

    getColumnConfig() {
        const {getLockedIndicatorProps, detailLinkPrefix, renderStateAndMapped} = this.props;
        const baseConfig = columnConfig(getLockedIndicatorProps, detailLinkPrefix || '/media-items', renderStateAndMapped)
            // Trim the Title column so the added checkbox column keeps the row total at ~100%.
            .map((column) => (column.dataKey === 'title' ? {...column, width: '19%'} : column));

        return [this.getSelectColumn(), ...baseConfig];
    }

    getSelectColumn() {
        return {
            dataKey: 'guid',
            width: '3%',
            className: 'fvod-ordering-table--select-cell',
            renderTitleCallback: this.renderSelectAllHeader,
            renderRowCallback: this.renderRowCheckbox,
        };
    }

    getTableWithSortProps() {
        const {fvodData} = this.state;
        return {
            columnConfig: this.getColumnConfig(),
            draggable: true,
            localSort: true,
            noSort: true,
            onDragEnd: this.onDragEnd,
            onRowContextMenu: this.handleRowContextMenu,
            rowColorCallback: this.rowColorCallback,
            rowDetails: this.getRowDetails(),
            searchData: fvodData,
            sort: [],
        };
    }

    getRowDetails() {
        return {
            whitelistKeys: [
                'description',
                'releaseYear',
                'originalAirDate',
                'seasonNumber',
                'episodeNumber',
                'duration',
                'rating',
                'lang',
                'genres',
                'createdBy',
                'lastModifiedBy',
            ],
        };
    }

    rowColorCallback = (dataRow) => {
        return dataRow.fvodOrdered
            ? { color: '#dbffc3', text: 'Ordered' }
            : { color: '#fdcece', text: 'Not Ordered' };
    };

    onDragEnd = (result) => {
        const {destination, source} = result;

        if (!destination) {
            return;
        }
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        this.setState(({fvodData}) => {
            const tempData = JSON.parse(JSON.stringify(fvodData));
            tempData.splice(destination.index, 0, tempData.splice(source.index, 1)[0]);

            return {fvodData: tempData};
        });
    };

    // --- Multiselect ---------------------------------------------------------

    isSelected = (dataRow) => {
        const {selectedGuids} = this.state;
        return !!selectedGuids[dataRow.guid];
    };

    getSelectedCount = () => {
        const {selectedGuids} = this.state;
        return Object.keys(selectedGuids).length;
    };

    toggleRowSelect = (dataRow) => (event) => {
        event.stopPropagation();
        this.setState(({selectedGuids}) => {
            const next = {...selectedGuids};
            if (next[dataRow.guid]) {
                delete next[dataRow.guid];
            } else {
                next[dataRow.guid] = true;
            }
            return {selectedGuids: next};
        });
    };

    toggleSelectAll = (event) => {
        event.stopPropagation();
        this.setState(({fvodData, selectedGuids}) => {
            const allSelected = fvodData.length > 0 && Object.keys(selectedGuids).length === fvodData.length;
            if (allSelected) {
                return {selectedGuids: {}};
            }
            const next = {};
            fvodData.forEach((item) => {
                next[item.guid] = true;
            });
            return {selectedGuids: next};
        });
    };

    // --- Move to top / bottom ------------------------------------------------

    moveSelected = (position) => {
        this.setState(
            ({fvodData, selectedGuids}) => {
                if (Object.keys(selectedGuids).length === 0) {
                    return null;
                }
                // Preserve current relative order for both groups.
                const selectedItems = fvodData.filter((item) => selectedGuids[item.guid]);
                const rest = fvodData.filter((item) => !selectedGuids[item.guid]);

                if (selectedItems.length === 0) {
                    return null;
                }

                const nextData = position === 'top' ? [...selectedItems, ...rest] : [...rest, ...selectedItems];

                return {fvodData: nextData, contextMenu: null};
            },
            () => this.scrollToEnd(position)
        );
    };

    setSelectedOrdered = (fvodOrdered) => {
        const {selectedGuids} = this.state;
        if (Object.keys(selectedGuids).length === 0) {
            return;
        }
        this.setState(
            ({fvodData}) => {
                // Mark selected assets as ordered/unordered.
                const nextData = fvodData.map((item) =>
                    (selectedGuids[item.guid] ? {...item, fvodOrdered} : item)
                );
                return {fvodData: nextData, contextMenu: null};
            },
            () => {
                // Order/Unorder immediately persist (POST) the currently-ordered assets,
                // same endpoint/format as Save Order. Do NOT re-fetch here: the local
                // fvodOrdered state we just set is authoritative for the row color, and a
                // refetch would overwrite the just-applied change.
                const {fvodData} = this.state;
                const orderedItems = fvodData.filter((item) => item.fvodOrdered);
                this.persistMediaItems(this.buildSavePayload(orderedItems), {refetch: false});
            }
        );
    };

    orderSelected = () => this.setSelectedOrdered(true);

    unorderSelected = () => this.setSelectedOrdered(false);

    // Order/Unorder visibility is driven by the composition of the selected set:
    // - only ordered rows selected   -> Unorder only
    // - only unordered rows selected  -> Order only
    // - a mix of both                 -> show both
    getSelectionOrderState = () => {
        const {fvodData, selectedGuids} = this.state;
        const selected = fvodData.filter((item) => selectedGuids[item.guid]);
        return {
            hasOrdered: selected.some((item) => item.fvodOrdered),
            hasUnordered: selected.some((item) => !item.fvodOrdered),
        };
    };

    scrollToEnd = (position) => {
        const root = this.rootRef.current;
        // The scrollable list container is the droppable wrapper.
        const container = root && root.querySelector('.table-with-sort--droppable');
        if (!container) {
            return;
        }
        container.scrollTo({
            top: position === 'top' ? 0 : container.scrollHeight,
            behavior: 'smooth',
        });
    };

    // --- Context menu --------------------------------------------------------

    getClampedMenuPosition = (clientX, clientY) => {
        // Keep the menu fully within the viewport (no overflow off the page edges).
        const menuWidth = 200;
        const menuHeight = 170;
        const pad = 8;
        const maxX = window.innerWidth - menuWidth - pad;
        const maxY = window.innerHeight - menuHeight - pad;
        return {
            x: Math.max(pad, Math.min(clientX, maxX)),
            y: Math.max(pad, Math.min(clientY, maxY)),
        };
    };

    handleRowContextMenu = (dataRow, event) => {
        event.preventDefault();
        const {x, y} = this.getClampedMenuPosition(event.clientX, event.clientY);
        this.setState(({selectedGuids}) => {
            // Right-clicking an unselected row selects just that row.
            const next = selectedGuids[dataRow.guid] ? selectedGuids : {[dataRow.guid]: true};
            return {
                selectedGuids: next,
                contextMenu: {x, y},
            };
        });
    };

    closeContextMenu = (event) => {
        const {contextMenu} = this.state;
        if (!contextMenu) {
            return;
        }
        // Ignore clicks that happen inside the menu itself.
        if (event && this.menuRef.current && this.menuRef.current.contains(event.target)) {
            return;
        }
        this.setState({contextMenu: null});
    };

    handleKeyDown = (event) => {
        if (!event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }
        if (this.getSelectedCount() === 0) {
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveSelected('top');
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveSelected('bottom');
        }
    };

    // The twister POST /mediaitems/fvodOrdering only persists idFromPartner + title
    // (FvodOrderService#addToFvodReportDto). Sending the full MediaItem graph back
    // fails to deserialize on the backend (500), so send just those two fields.
    buildSavePayload = (items) => items.map(({idFromPartner, title}) => ({idFromPartner, title}));

    saveOrder = () => {
        const {selectedGuids} = this.state;
        const hasSelection = Object.keys(selectedGuids).length > 0;

        if (hasSelection) {
            // With a selection: mark the selected assets as ordered, then persist the
            // ordered set (existing ordered + newly selected) and refresh.
            this.setState(
                ({fvodData}) => ({
                    fvodData: fvodData.map((item) =>
                        (selectedGuids[item.guid] ? {...item, fvodOrdered: true} : item)
                    ),
                }),
                () => {
                    const {fvodData} = this.state;
                    const orderedItems = fvodData.filter((item) => item.fvodOrdered);
                    this.persistMediaItems(this.buildSavePayload(orderedItems));
                }
            );
            return;
        }

        // No selection: save the entire list as-is and refresh.
        const {fvodData} = this.state;
        this.persistMediaItems(this.buildSavePayload(fvodData));
    };

    // Shared POST used by Save Order, Order and Unorder.
    // refetch=true (Save Order) reloads fresh data from the backend; refetch=false
    // (Order/Unorder) keeps the optimistic local state so the row color reflects the change.
    persistMediaItems = (mediaItems, {refetch = true} = {}) => {
        this.setState({fetching: true, error: null});

        const dispatchFetched = () => {
            if (refetch) {
                // Reload fresh data from the backend so the UI reflects the saved order/state.
                this.fetchFvodData();
                return;
            }
            this.setState(({fvodData}) => ({
                originalData: JSON.parse(JSON.stringify(fvodData)),
                selectedGuids: {},
                contextMenu: null,
                fetching: false,
            }));
        };

        const dispatchError = (error) => {
            this.setState({
                error: error?.message || error || 'Failed to save FVOD ordering',
                fetching: false,
            });
        };

        const actionParams = {
            servicesConfig: servicesConfig.fvodOrderingSave,
            dispatchFetched,
            dispatchError,
        };

        actionsServiceCaller(actionParams, {mediaItems});
    };

    cancelOrder = () => {
        this.setState((prevState) => ({
            fvodData: JSON.parse(JSON.stringify(prevState.originalData)),
            selectedGuids: {},
            contextMenu: null,
        }));
    };

    fetchFvodData() {
        this.setState({fetching: true, error: null});

        const dispatchFetched = (data) => {
            const items = data?.mediaItems || [];
            const sorted = this.sortFvodData(Array.isArray(items) ? items : []);
            this.setState({
                fvodData: sorted,
                originalData: JSON.parse(JSON.stringify(sorted)),
                selectedGuids: {},
                contextMenu: null,
                fetching: false,
            });
        };

        const dispatchError = (error) => {
            this.setState({
                error: error?.message || error || 'Failed to fetch FVOD ordering data',
                fetching: false,
            });
        };

        const actionParams = {
            servicesConfig: servicesConfig.fvodOrdering,
            dispatchFetched,
            dispatchError,
        };

        actionsServiceCaller(actionParams);
    }

    sortFvodData(items) {
        const unordered = items.filter((item) => !item.fvodOrdered);
        const ordered = items.filter((item) => item.fvodOrdered);

        if (ordered.length === 0) {
            // All unordered: sort by lastUpdated desc
            return unordered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
        }
        if (unordered.length === 0) {
            // All ordered: keep original order from backend
            return ordered;
        }
        // Mixed: ordered at top (in saved order), unordered at bottom sorted by lastUpdated desc
        unordered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
        return [...ordered, ...unordered];
    }

    // --- Render helpers ------------------------------------------------------

    // Stable tooltip renderers (defined once) to avoid recreating components on each render.
    renderSelectAllTooltip = () => <div>Select all / clear selection</div>;

    renderMoveTopTooltip = () => <div>Move selected records to the top of the list (Alt+↑)</div>;

    renderMoveBottomTooltip = () => <div>Move selected records to the bottom of the list (Alt+↓)</div>;

    renderOrderTooltip = () => <div>Order selected records and save immediately</div>;

    renderUnorderTooltip = () => <div>Unorder selected records and save immediately</div>;

    renderRowCheckbox = (dataRow) => (
        <input
            aria-label="Select record for reordering"
            checked={this.isSelected(dataRow)}
            title="Select record for reordering"
            type="checkbox"
            onChange={this.toggleRowSelect(dataRow)}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        />
    );

    renderSelectAllHeader = (column, key) => {
        const {fvodData, selectedGuids} = this.state;
        const selectedCount = Object.keys(selectedGuids).length;
        const allSelected = fvodData.length > 0 && selectedCount === fvodData.length;

        return (
            <div className="table-with-sort--cell fvod-ordering-table--select-cell" key={key} style={{width: column.width}}>
                <TooltipTrigger displayDirection="up" tooltipRenderer={this.renderSelectAllTooltip}>
                    <input
                        aria-label="Select all records"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) {
                                el.indeterminate = selectedCount > 0 && !allSelected;
                            }
                        }}
                        type="checkbox"
                        onChange={this.toggleSelectAll}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </TooltipTrigger>
            </div>
        );
    };

    renderContextMenu() {
        const {contextMenu} = this.state;
        if (!contextMenu) {
            return null;
        }

        const {hasOrdered, hasUnordered} = this.getSelectionOrderState();

        return (
            <ul
                className="fvod-ordering-table--context-menu"
                ref={this.menuRef}
                style={{left: contextMenu.x, top: contextMenu.y}}
            >
                <li>
                    <TooltipTrigger displayDirection="right" tooltipRenderer={this.renderMoveTopTooltip}>
                        <button
                            className="fvod-ordering-table--context-menu-item"
                            title="Move to top (Alt+↑)"
                            type="button"
                            onClick={() => this.moveSelected('top')}
                        >
                            Move to top
                        </button>
                    </TooltipTrigger>
                </li>
                <li>
                    <TooltipTrigger displayDirection="right" tooltipRenderer={this.renderMoveBottomTooltip}>
                        <button
                            className="fvod-ordering-table--context-menu-item"
                            title="Move to bottom (Alt+↓)"
                            type="button"
                            onClick={() => this.moveSelected('bottom')}
                        >
                            Move to bottom
                        </button>
                    </TooltipTrigger>
                </li>
                {hasUnordered && (
                    <li>
                        <TooltipTrigger displayDirection="right" tooltipRenderer={this.renderOrderTooltip}>
                            <button
                                className="fvod-ordering-table--context-menu-item"
                                title="Order (saves the change)"
                                type="button"
                                onClick={this.orderSelected}
                            >
                                Order
                            </button>
                        </TooltipTrigger>
                    </li>
                )}
                {hasOrdered && (
                    <li>
                        <TooltipTrigger displayDirection="right" tooltipRenderer={this.renderUnorderTooltip}>
                            <button
                                className="fvod-ordering-table--context-menu-item"
                                title="Unorder (saves the change)"
                                type="button"
                                onClick={this.unorderSelected}
                            >
                                Unorder
                            </button>
                        </TooltipTrigger>
                    </li>
                )}
            </ul>
        );
    }

    render() {
        const {fvodData, fetching, error} = this.state;

        return (
            <div className="fvod-ordering-table" ref={this.rootRef}>
                <Loading loading={fetching} />
                {error && <Error error={error} onClear={() => this.setState({error: null})} />}
                {fvodData.length > 0 && <TableWithSort {...this.getTableWithSortProps()} />}
                {!fetching && !error && fvodData.length === 0 && (
                    <Error error="No FVOD ordering data available." />
                )}
                {this.renderContextMenu()}
            </div>
        );
    }
}

FvodOrderingTable.propTypes = {
    detailLinkPrefix: PropTypes.string,
    getLockedIndicatorProps: PropTypes.func,
    renderStateAndMapped: PropTypes.func,
};

export default FvodOrderingTable;
